import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { dateLocale } from '../../i18n/i18n';
import { useQuery } from 'react-query';
import { getPaginatedCashflowTransactions } from '../../services/cashflowTransactions';
import { onQuerySuccess } from '../../utils/queryResponse';
import { rsdFormat } from '../../utils/helper';
import Pages from '../../components/pages';
import Skeleton from '../../components/skeleton';
import CashFlowTransactionsFilter from './cashflowTransactionsFilter';

/* Icon for income (arrow up) */
const IncomeIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
        <line x1="12" y1="19" x2="12" y2="5" />
        <polyline points="5 12 12 5 19 12" />
    </svg>
);

/* Icon for expense (arrow down) */
const ExpenseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
        <line x1="12" y1="5" x2="12" y2="19" />
        <polyline points="19 12 12 19 5 12" />
    </svg>
);

/* Format date as "19 Jun 2026" */
const fmtDate = (raw) => {
    if (!raw) return '—';
    const d = new Date(raw);
    return d.toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short', year: 'numeric' });
};

/* Determine whether a transaction is income based on available fields */
const isIncome = (tx) => {
    if (tx.type != null)           return tx.type === 'income' || tx.type === 'Income';
    if (tx.transactionType != null) return tx.transactionType === 'income' || tx.transactionType === 'Income';
    if (tx.isIncome != null)       return !!tx.isIncome;
    // fallback: positive amount = income
    return Number(tx.amount) >= 0;
};

const PAGE_SIZES = [10, 20, 50, 100];

const TransactionRow = ({ tx }) => {
    const { t } = useTranslation();
    const income = isIncome(tx);
    return (
        <div className="row-sbr">
            {/* Avatar / type icon */}
            <div className="a" style={{
                background: income ? 'var(--good-bg)' : 'var(--bad-bg)',
                border: `1px solid ${income ? 'var(--good-bd)' : 'var(--bad-bd)'}`,
                color: income ? 'var(--good)' : 'var(--bad)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
            }}>
                {income ? <IncomeIcon /> : <ExpenseIcon />}
            </div>

            {/* Description + category */}
            <div className="info">
                <div className="nm">{tx.description || tx.category || (income ? t('cashflow.incomeKpi') : t('cashflow.expensesKpi'))}</div>
                <div className="sub">
                    {tx.category && tx.description ? tx.category : null}
                    {tx.category && tx.description ? ' · ' : null}
                    {fmtDate(tx.date || tx.createdAt)}
                    {tx.paymentMethod ? ` · ${tx.paymentMethod}` : null}
                </div>
            </div>

            {/* Amount */}
            <div className="rt">
                <div className="v num" style={{ color: income ? 'var(--good)' : 'var(--bad)' }}>
                    {income ? '+' : '−'}{rsdFormat(Math.abs(Number(tx.amount)))}
                </div>
                {tx.notes && (
                    <div className="vs" title={tx.notes} style={{
                        maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {tx.notes}
                    </div>
                )}
            </div>
        </div>
    );
};

const SkeletonRow = () => (
    <div className="row-sbr" style={{ gap: 12 }}>
        <Skeleton width={38} height={38} style={{ borderRadius: 12, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
            <Skeleton width="55%" height={14} style={{ marginBottom: 6 }} />
            <Skeleton width="35%" height={12} />
        </div>
        <div style={{ textAlign: 'right' }}>
            <Skeleton width={90} height={14} style={{ marginBottom: 6 }} />
            <Skeleton width={60} height={11} />
        </div>
    </div>
);

const CashFlowTransactionsPage = () => {
    const { t } = useTranslation();
    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination]     = useState(null);
    const [pageNumber, setPageNumber]     = useState(1);
    const [pageSize, setPageSize]         = useState(20);
    const [filters, setFilters]           = useState(['', '']);

    const [startDate, endDate] = filters || ['', ''];

    const { data, isFetching } = useQuery(
        ['cashflow-transactions', 'paginated', pageNumber, pageSize, startDate, endDate],
        () => getPaginatedCashflowTransactions(pageNumber, pageSize, startDate || null, endDate || null),
        { staleTime: 30000, keepPreviousData: true }
    );

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setPagination(result);
            setTransactions(result.data ?? []);
        });
    }, [data]);

    const handleFilter = (newFilters) => {
        setFilters(newFilters);
        setPageNumber(1);
    };

    const handlePageSize = (e) => {
        setPageSize(Number(e.target.value));
        setPageNumber(1);
    };

    const totalRecords = pagination?.totalRecords ?? null;

    return (
        <div className="sbr">

            {/* Desktop header */}
            <div className="dhead">
                <div>
                    <div className="ttl">{t('cashflow.transactionsTitle')}</div>
                    <div className="sub">
                        {t('cashflow.transactionsSubtitle')}
                        {totalRecords != null ? ` · ${t('cashflow.records', { count: totalRecords.toLocaleString() })}` : ''}
                    </div>
                </div>
                <div className="act">
                    {/* Page size selector */}
                    <select
                        value={pageSize}
                        onChange={handlePageSize}
                        style={{
                            font: 'inherit', fontSize: '13px', fontWeight: 600,
                            background: 'var(--card)', border: '1px solid var(--line)',
                            borderRadius: 10, padding: '8px 12px', color: 'var(--text)',
                            cursor: 'pointer', appearance: 'none',
                        }}
                    >
                        {PAGE_SIZES.map(s => (
                            <option key={s} value={s}>{t('cashflow.perPage', { size: s })}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Filter */}
            <div style={{ marginBottom: 16 }}>
                <CashFlowTransactionsFilter onFilter={handleFilter} />
            </div>

            {/* Transaction list card */}
            <div className="card-sbr" style={{ padding: '0 18px' }}>

                {/* Loading skeletons */}
                {isFetching && transactions.length === 0 && (
                    Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                )}

                {/* Empty state */}
                {!isFetching && transactions.length === 0 && (
                    <div style={{
                        padding: '48px 0', textAlign: 'center',
                        color: 'var(--muted)', fontSize: '14px',
                    }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                            style={{ width: 40, height: 40, marginBottom: 12, color: 'var(--faint)', display: 'block', margin: '0 auto 12px' }}>
                            <rect x="2" y="5" width="20" height="14" rx="3" />
                            <line x1="2" y1="10" x2="22" y2="10" />
                        </svg>
                        {t('cashflow.noTransactionsFound')}
                    </div>
                )}

                {/* Rows */}
                {transactions.map((tx, i) => (
                    <TransactionRow key={tx.id ?? i} tx={tx} />
                ))}

                {/* Overlay shimmer while paginating */}
                {isFetching && transactions.length > 0 && (
                    <div style={{
                        padding: '10px 0 4px',
                        opacity: 0.5, pointerEvents: 'none',
                    }}>
                        {Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} />)}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pagination?.totalPages > 1 && (
                <div style={{ marginTop: 16 }}>
                    <Pages
                        paginationData={pagination}
                        changePage={setPageNumber}
                        pageNumber={pageNumber}
                    />
                </div>
            )}

        </div>
    );
};

export default CashFlowTransactionsPage;
